import { useState, useEffect } from 'react';
import { Check, Upload, Trash2, Star } from 'lucide-react';
import { Input } from '../ui/input';
import { ICON_MAP, ICON_BG_MAP, PRESET_COLORS } from './constants';
import EmbeddedPaymentBlock from './EmbeddedPaymentBlock';

/**
 * Renders ONE configured onboarding page (any type).
 * Type-specific blocks are split into small inline render helpers below for
 * readability while keeping all state at the parent level.
 */
export default function OnboardingPage({
  pg,
  fieldVal,
  formData,
  cardTextLight,
  primaryColor,
  secondaryColor,
  badgeFile,
  badgePreview,
  setField,
  setPrimaryColor,
  toggleMultiSelect,
  handleFileSelect,
  removeBadge,
  skipToSignup,
  showSkipLink,
  darkBg,
}) {
  if (!pg) return null;
  const IconComp = ICON_MAP[pg.icon] || Star;
  const iconColors = pg.icon_bg === 'gradient' ? null : (ICON_BG_MAP[pg.icon_bg] || ICON_BG_MAP.emerald);

  return (
    <div className="space-y-5" data-testid={`page-${pg.id}`}>
      {/* Header (icon + title + subtitle) */}
      <div className="text-center">
        {!pg.icon ? null : pg.type === 'color_picker' ? (
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
            <div className="w-6 h-6 rounded-full bg-white/30" />
          </div>
        ) : pg.type === 'text_input' && pg.field_key === 'display_name' ? (
          <div className={`w-14 h-14 rounded-full ${iconColors?.bg || 'bg-gray-100'} flex items-center justify-center mx-auto mb-3 text-2xl font-bold ${iconColors?.text || 'text-gray-700'}`}>
            {(formData.display_name || '?').charAt(0).toUpperCase()}
          </div>
        ) : (
          <div className={`w-14 h-14 rounded-full ${iconColors?.bg || 'bg-gray-100'} flex items-center justify-center mx-auto mb-3`}>
            <IconComp className={`w-7 h-7 ${iconColors?.text || 'text-gray-700'}`} />
          </div>
        )}
        <h2 className={`text-xl font-bold ${cardTextLight ? 'text-white' : 'text-gray-900'}`}>{pg.title}</h2>
        <p className={`onb-subtitle text-base sm:text-lg font-light leading-snug mt-2 ${cardTextLight ? 'text-white/70' : 'text-gray-700'}`}>{pg.subtitle}</p>
      </div>

      {pg.type === 'counter' && <CounterBlock pg={pg} fieldVal={fieldVal} setField={setField} />}
      {pg.type === 'multi_select' && <MultiSelectBlock pg={pg} fieldVal={fieldVal} darkBg={darkBg} toggleMultiSelect={toggleMultiSelect} />}
      {pg.type === 'single_select' && <SingleSelectBlock pg={pg} fieldVal={fieldVal} cardTextLight={cardTextLight} darkBg={darkBg} setField={setField} />}
      {pg.type === 'text_input' && <TextInputBlock pg={pg} fieldVal={fieldVal} cardTextLight={cardTextLight} setField={setField} />}
      {pg.type === 'email_capture' && <EmailCaptureBlock pg={pg} fieldVal={fieldVal} cardTextLight={cardTextLight} setField={setField} />}
      {pg.type === 'password_capture' && <PasswordCaptureBlock pg={pg} formData={formData} cardTextLight={cardTextLight} setField={setField} />}
      {pg.type === 'payment_capture' && <EmbeddedPaymentBlock darkBg={darkBg} />}
      {pg.type === 'file_upload' && <FileUploadBlock pg={pg} IconComp={IconComp} badgeFile={badgeFile} badgePreview={badgePreview} handleFileSelect={handleFileSelect} removeBadge={removeBadge} />}
      {pg.type === 'color_picker' && <ColorPickerBlock formData={formData} cardTextLight={cardTextLight} setPrimaryColor={setPrimaryColor} />}
      {pg.type === 'intro' && (
        <div className="text-center space-y-3" data-testid="intro-body">
          {pg.body_text && pg.body_text.trim() && (
            <p
              className={`onb-body-text text-sm leading-relaxed whitespace-pre-line ${cardTextLight ? 'text-white/85' : 'text-gray-700'}`}
              data-testid="intro-body-text"
            >
              {pg.body_text}
            </p>
          )}
          {showSkipLink && pg.show_skip_to_signup && (
            <button
              type="button"
              onClick={skipToSignup}
              className={`inline-flex items-center gap-1 text-xs font-semibold underline-offset-4 hover:underline transition-colors ${
                cardTextLight ? 'text-white/80 hover:text-white' : 'text-emerald-700 hover:text-emerald-800'
              }`}
              data-testid="intro-skip-to-signup"
            >
              {pg.skip_to_signup_text || 'Skip ahead and create your account →'}
            </button>
          )}
        </div>
      )}
      {pg.type === 'features' && (pg.bullets || []).length > 0 && <FeaturesBlock pg={pg} cardTextLight={cardTextLight} />}
    </div>
  );
}

// ── Counter ──
function CounterBlock({ pg, fieldVal, setField }) {
  // Use ?? so 0 is a valid value (admins can configure counter_min=0). Default
  // display is 0 when neither fieldVal nor counter_default is set — user taps
  // + to set their actual number.
  const currentVal = fieldVal ?? pg.counter_default ?? 0;
  const minVal = pg.counter_min ?? 0;
  const maxVal = pg.counter_max ?? 50;
  const isAtMax = currentVal >= maxVal;
  let labelText = pg.counter_label || '';
  if (labelText) {
    const isPlural = labelText.toLowerCase().endsWith('s');
    if (currentVal === 1 && isPlural) labelText = labelText.slice(0, -1);
    else if (currentVal !== 1 && !isPlural) labelText += 's';
  }
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center justify-center gap-4">
        <button
          className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 active:scale-90 flex items-center justify-center text-gray-700 text-2xl font-bold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => setField(pg.field_key, Math.max(minVal, currentVal - 1))}
          disabled={currentVal <= minVal}
          data-testid={`${pg.id}-down`}
        >&minus;</button>
        <span
          className="text-5xl font-black text-gray-900 font-mono inline-flex items-center justify-center w-20 leading-none"
          data-testid={`${pg.id}-value`}
        >
          {isAtMax ? `${maxVal}+` : currentVal}
        </span>
        <button
          className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 active:scale-90 flex items-center justify-center text-gray-700 text-2xl font-bold transition-all duration-150"
          onClick={() => setField(pg.field_key, Math.min(maxVal, currentVal + 1))}
          data-testid={`${pg.id}-up`}
        >+</button>
      </div>
      {labelText && <p className="text-xs text-gray-400">{labelText}</p>}
    </div>
  );
}

// ── Multi-select ──
function MultiSelectBlock({ pg, fieldVal, darkBg, toggleMultiSelect }) {
  const isAgeGroups = pg.field_key === 'age_groups';
  const gridCls = isAgeGroups
    ? 'grid-cols-4 gap-1.5'
    : ((pg.options || []).length > 8 ? 'grid-cols-3 gap-1.5' : 'grid-cols-2 gap-2');
  // Selected option colour — slate blue-grey on dark bg, dark slate grey on light bg.
  // Keeps the green Next button as the only green CTA on the page.
  const selectedCls = darkBg
    ? 'bg-[#64748B] text-white border-[#64748B] shadow-sm'
    : 'bg-[#262626] text-white border-[#262626] shadow-sm';
  const unselectedCls = darkBg
    ? 'bg-white/10 text-white/85 border-white/15 hover:bg-white/15'
    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300';
  return (
    <div className={`mt-6 grid ${gridCls}`}>
      {(pg.options || []).map((opt, idx) => {
        const selected = Array.isArray(fieldVal) && fieldVal.includes(opt);
        const isLast = idx === (pg.options || []).length - 1;
        const fullWidth = isAgeGroups && isLast;
        return (
          <button
            key={opt}
            onClick={() => toggleMultiSelect(pg.field_key, opt)}
            className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${fullWidth ? 'col-span-4' : ''} ${
              selected ? selectedCls : unselectedCls
            }`}
            data-testid={`${pg.id}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ── Single-select ──
function SingleSelectBlock({ pg, fieldVal, cardTextLight, darkBg, setField }) {
  const len = (pg.options || []).length;
  const gridCls = len <= 4 ? 'grid-cols-2 gap-2' : 'grid-cols-3 gap-1.5';
  // Same palette logic as MultiSelectBlock — keeps green reserved for the Next CTA.
  const selectedCls = darkBg
    ? 'bg-[#64748B] text-white border-[#64748B] shadow-sm'
    : 'bg-[#262626] text-white border-[#262626] shadow-sm';
  const unselectedCls = darkBg
    ? 'bg-white/10 text-white/85 border-white/15 hover:bg-white/15'
    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300';
  return (
    <>
      {pg.body_text && pg.body_text.trim() && (
        <p
          className={`onb-body-text text-sm leading-relaxed whitespace-pre-line text-center mt-2 ${cardTextLight ? 'text-white/85' : 'text-gray-700'}`}
          data-testid={`${pg.id}-body-text`}
        >
          {pg.body_text}
        </p>
      )}
      <div className={`mt-6 grid ${gridCls}`}>
        {(pg.options || []).map((opt) => {
          const selected = fieldVal === opt;
          return (
            <button
              key={opt}
              onClick={() => setField(pg.field_key, opt)}
              className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                selected ? selectedCls : unselectedCls
              }`}
              data-testid={`${pg.id}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Text input + helper text ──
function TextInputBlock({ pg, fieldVal, cardTextLight, setField }) {
  return (
    <div className="mt-6">
      <Input
        value={fieldVal || ''}
        onChange={e => setField(pg.field_key, e.target.value)}
        placeholder={pg.placeholder || 'Enter text...'}
        className="h-12 text-base text-center rounded-xl placeholder:text-sm placeholder:text-gray-400"
        autoFocus={typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches}
        data-testid={`${pg.id}-input`}
      />
      {pg.helper_text && (
        <p className={`text-xs text-center leading-relaxed mt-2.5 ${cardTextLight ? 'text-white/55' : 'text-gray-500'}`} data-testid={`${pg.id}-helper`}>{pg.helper_text}</p>
      )}
    </div>
  );
}

// ── File upload (badge) ──
function FileUploadBlock({ pg, IconComp, badgeFile, badgePreview, handleFileSelect, removeBadge }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
        {badgePreview ? (
          <img src={badgePreview} alt="Upload preview" className="w-full h-full object-contain" />
        ) : (
          <IconComp className="w-10 h-10 text-gray-300" />
        )}
      </div>
      <div className="flex gap-2">
        <label className="cursor-pointer">
          <input type="file" accept="image/*" onChange={e => handleFileSelect(e.target.files[0])} className="hidden" />
          <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors">
            <Upload className="w-4 h-4" /> {badgeFile ? 'Change' : 'Upload'}
          </span>
        </label>
        {badgeFile && (
          <button onClick={removeBadge} className="px-3 py-2 text-sm text-red-500 bg-red-50 rounded-xl hover:bg-red-100 flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

// ── Colour picker (Main Colour, 8 swatches in 2×4) ──
function ColorPickerBlock({ formData, cardTextLight, setPrimaryColor }) {
  return (
    <div className="space-y-4 mt-6">
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 text-center ${cardTextLight ? 'text-white/70' : 'text-gray-500'}`}>Main Colour</p>
        <div className="grid grid-cols-4 gap-3 sm:gap-2">
          {PRESET_COLORS.map(c => {
            const selected = formData.club_colours?.primary === c.value;
            return (
              <button
                key={`p-${c.value}`}
                onClick={() => setPrimaryColor(selected ? '' : c.value)}
                className={`w-4/5 sm:w-full mx-auto aspect-square rounded-lg transition-all border-2 ${
                  selected ? `${cardTextLight ? 'border-white' : 'border-gray-900'} scale-110 shadow-md` : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: c.value }}
                title={selected ? `${c.name} (click to deselect)` : c.name}
                data-testid={`primary-${c.name.toLowerCase()}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Email Capture (email input + Google login button + signup counter) ──
function EmailCaptureBlock({ pg, fieldVal, cardTextLight, setField }) {
  const [counter, setCounter] = useState(null);
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/stats/signup-counter`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCounter(d))
      .catch(() => {});
  }, []);
  // Decide which counter line to show — only show if numbers are impressive enough
  const counterLine = (() => {
    if (!counter) return null;
    if (counter.today >= 5) {
      return `${counter.today} coaches signed up today`;
    }
    if (counter.week >= 3) {
      return `${counter.week} coaches joined this week`;
    }
    return null;
  })();

  const handleGoogleLogin = () => {
    try { window.localStorage.setItem('onb_pre_google_redirect', '1'); } catch {}
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <div className="space-y-4 mt-6">
      <Input
        type="email"
        value={fieldVal || ''}
        onChange={(e) => setField(pg.field_key, e.target.value)}
        placeholder={pg.placeholder || 'you@yourclub.com'}
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        className={`h-12 text-base rounded-xl ${cardTextLight ? 'bg-white/10 text-white border-white/20 placeholder:text-white/40' : ''}`}
        data-testid="onb-email-input"
      />
      {pg.helper_text && (
        <p className={`text-xs text-center pt-2 ${cardTextLight ? 'text-white/60' : 'text-gray-500'}`}>
          {pg.helper_text}
        </p>
      )}
      <div className="flex items-center justify-center my-2">
        <span className={`text-[10px] font-semibold tracking-widest uppercase ${cardTextLight ? 'text-white/50' : 'text-gray-400'}`}>or</span>
      </div>
      <button
        type="button"
        onClick={handleGoogleLogin}
        className={`w-full h-12 rounded-xl border flex items-center justify-center gap-2.5 font-medium text-sm transition-colors ${
          cardTextLight
            ? 'bg-white/10 hover:bg-white/15 border-white/20 text-white'
            : 'bg-white hover:bg-gray-50 border-gray-200 text-[#1F2937]'
        }`}
        data-testid="onb-google-btn"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.51 8.18c0-.55-.05-1.08-.13-1.59H9v3.02h4.21c-.18.97-.74 1.79-1.57 2.34v1.95h2.54c1.49-1.37 2.34-3.4 2.34-5.72z" fill="#4285F4"/>
          <path d="M9 17c2.13 0 3.91-.71 5.21-1.91l-2.54-1.95c-.7.47-1.6.75-2.67.75-2.05 0-3.78-1.38-4.4-3.24H1.97v2.02C3.27 14.94 5.91 17 9 17z" fill="#34A853"/>
          <path d="M4.6 10.65c-.16-.47-.25-.97-.25-1.5s.09-1.03.25-1.5V5.62H1.97A8 8 0 001 9c0 1.21.29 2.36.97 3.38l2.63-2.05z" fill="#FBBC05"/>
          <path d="M9 4.04c1.16 0 2.2.4 3.02 1.18l2.26-2.26C12.9 1.74 11.13 1 9 1 5.91 1 3.27 3.06 1.97 5.62L4.6 7.65C5.22 5.79 6.95 4.04 9 4.04z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>
      {/* Trust counter — social proof below the Google button */}
      {counterLine && (
        <div
          className={`flex items-center justify-center gap-1.5 mt-2 text-[11px] font-medium ${
            cardTextLight ? 'text-white/70' : 'text-gray-500'
          }`}
          data-testid="onb-signup-counter"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          {counterLine}
        </div>
      )}
    </div>
  );
}

// ── Password Capture (password + confirm) ──
function PasswordCaptureBlock({ pg, formData, cardTextLight, setField }) {
  const pwField = pg.field_key || 'password';
  const confirmField = `${pwField}_confirm`;
  const password = formData[pwField] || '';
  const confirm = formData[confirmField] || '';
  const mismatch = confirm && password !== confirm;
  return (
    <div className="space-y-3">
      <Input
        type="password"
        value={password}
        onChange={(e) => setField(pwField, e.target.value)}
        placeholder={pg.placeholder || 'Password (min 6 characters)'}
        autoComplete="new-password"
        className={`h-12 text-base rounded-xl ${cardTextLight ? 'bg-white/10 text-white border-white/20 placeholder:text-white/40' : ''}`}
        data-testid="onb-password-input"
      />
      <Input
        type="password"
        value={confirm}
        onChange={(e) => setField(confirmField, e.target.value)}
        placeholder="Confirm password"
        autoComplete="new-password"
        className={`h-12 text-base rounded-xl ${
          mismatch ? 'border-red-300' : ''
        } ${cardTextLight ? 'bg-white/10 text-white border-white/20 placeholder:text-white/40' : ''}`}
        data-testid="onb-password-confirm"
      />
      {mismatch && (
        <p className="text-xs text-red-500 text-center">Passwords do not match</p>
      )}
      {pg.helper_text && !mismatch && (
        <p className={`text-xs text-center ${cardTextLight ? 'text-white/60' : 'text-gray-500'}`}>
          {pg.helper_text}
        </p>
      )}
    </div>
  );
}

function FeaturesBlock({ pg, cardTextLight }) {
  return (
    <div className="mt-4 flex justify-center">
      <ul className="space-y-5 inline-block text-left max-w-full" data-testid="features-bullets">
        {(pg.bullets || []).map((b, bIdx) => (
          <li
            key={bIdx}
            className="flex items-start gap-3 animate-fadeInRow opacity-0"
            style={{ animationDelay: `${bIdx * 120 + 200}ms`, animationFillMode: 'forwards' }}
            data-testid={`feature-bullet-${bIdx}`}
          >
            <Check
              className={`mt-0.5 flex-shrink-0 w-[18px] h-[18px] ${cardTextLight ? 'text-white' : 'text-[#007a33]'}`}
              strokeWidth={3}
            />
            <span className={`onb-body-text text-sm leading-relaxed ${cardTextLight ? 'text-white/90' : 'text-gray-700'}`}>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
